import { usePersonStore } from '~/stores/person';
import type { BaseAPIResponse } from '~/types/api';
import type { ConversationMessage } from '~/types/companies/conversations';
import type { CompanyExposure, Exposure, PersonExposure } from '~/types/person/details';
import type { PotentialExposuresResponseData, SuppressionRecord, SuppressionsApiResponse } from '~/types/person/vault';
import { ApiError } from '~/utils/errors';

// Pure API service for exposure-related operations - no state management
export const exposuresService = {
    async getExposure(personId: number, exposureId: string): Promise<Exposure> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<any>>(
            nuxtApp.$api.routes.people.exposures.exposure(personId, exposureId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposure details', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No exposure details received', 404);
        }

        return data.data;
    },

    async getExposures(personId: number, limit: number = 50): Promise<PersonExposure[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<PersonExposure[]>>(
            nuxtApp.$api.routes.people.exposures.list(personId, limit)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposures', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No exposures received', 404);
        }

        return data.data;
    },

    async getPotentialExposures(personId: number): Promise<PotentialExposuresResponseData> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<PotentialExposuresResponseData>>(
            nuxtApp.$api.routes.people.exposures.data_exposed(personId),
            {
                method: 'GET',
            }
        );
        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch potential exposures', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No potential exposures data received', 404);
        }
        return data.data;
    },

    async updateExposure(type: string, value: any, isMyExposure: boolean): Promise<SuppressionRecord> {
        const nuxtApp = useNuxtApp();

        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<SuppressionRecord>>(
                nuxtApp.$api.routes.me.exposures.suppressions.update_suppressions(),
                {
                    method: 'PUT',
                    body: {
                        type: type,
                        data: value,
                        me: isMyExposure,
                    },
                }
            );

            if (status !== 201 || !data) {
                throw new ApiError(message || 'Failed to update exposure', status);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getSuppressedRecords(): Promise<SuppressionRecord[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<SuppressionsApiResponse>>(
            nuxtApp.$api.routes.me.exposures.suppressions.get_suppressions(),
            {
                method: 'GET',
            }
        );

        if (status !== 200) {
            throw new ApiError('Could not fetch suppressed records', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No suppressed records data received', 404);
        }

        const parsedRecords: SuppressionRecord[] = [];

        // Process each record - backend now handles JSON decoding
        for (const record of data.data as any) {
            // If relatives, skip for now (as per original logic)
            if (record.type === 'relatives') {
                continue;
            }

            // Backend already decodes JSON, so we don't need to parse again
            // But keep backward compatibility in case data is still a string
            let processedData = record.data;
            if (typeof record.data === 'string' && record.data.match(/^{.*}$/)) {
                try {
                    processedData = JSON.parse(record.data);
                } catch (e) {
                    // If parsing fails, keep as string
                    processedData = record.data;
                }
            }

            parsedRecords.push({
                ...record,
                data: processedData,
            });
        }

        return parsedRecords;
    },

    async deleteSuppressedRecord(suppressionId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.me.exposures.suppressions.remove_suppression(suppressionId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to delete suppressed record', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getExposureDetails(personId: number): Promise<CompanyExposure[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<CompanyExposure[]>>(
            nuxtApp.$api.routes.people.exposures.detail(personId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposure details', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No exposure details data received', 404);
        }

        return data.data;
    },

    async getActiveExposures(personId: number): Promise<CompanyExposure[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<CompanyExposure[]>(
            nuxtApp.$api.routes.people.exposures.data_exposed(personId)
        );

        if (status !== 201 || !data) {
            throw new ApiError(message || 'Failed to fetch active exposures', status);
        }

        if (!data) {
            throw new ApiError('No active exposures data received', 404);
        }

        return data;
    },

    async getSuppressions(): Promise<any> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi(nuxtApp.$api.routes.me.exposures.suppressions.get_suppressions());

        if (status !== 200 || !data) {
            throw new ApiError(message || 'Could not fetch suppressions', status);
        }

        if (!data) {
            throw new ApiError('No suppressions data received', 404);
        }

        return data;
    },

    async getHistoryExport(personId: number): Promise<{url: string}> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<{url: string}>(nuxtApp.$api.routes.people.exposures.history_export(personId));

        if (status !== 200) {
            throw new ApiError(message || 'Failed to export history', status);
        }

        if (!data?.url) {
            throw new ApiError('No download URL received', 404);
        }

        return data;
    },

    async requestDeletion(companyId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        const { status, message } = await usePbApi(
            nuxtApp.$api.routes.people.companies.untrust_company(personStore.person?.id || 0, companyId),
            {
                method: 'POST',
                body: {
                    delete: true,
                },
            }
        );

        if (status !== 201) {
            throw new ApiError(message || 'Failed to request deletion', status);
        }
    },

    async getExposureHistory(personId: number, exposureId: string): Promise<ConversationMessage[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<ConversationMessage[]>>(
            nuxtApp.$api.routes.people.exposures.exposure_history(personId, exposureId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposure history', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No history data received', 404);
        }

        return data.data;
    },
};
